import catalog from "./demo-tenants.catalog.json";

export type DemoVertical = "generic" | "rh" | "inventory" | "edu" | "hospitol" | "billing" | "crm";

export interface DemoProfileAccount {
  email: string;
  firstName: string;
  lastName: string;
  module: string;
  roleSlug: string;
  label: string;
  moduleAttributes?: Record<string, string>;
}

export interface SpecialtyDemoTenant {
  slug: string;
  name: string;
  legalName: string;
  vertical: DemoVertical;
  label: string;
  modules: string[];
  adminEmail: string;
  adminFirstName: string;
  adminLastName: string;
  profileAccounts: DemoProfileAccount[];
  additionalTenantAdmins?: Array<{
    email: string;
    firstName: string;
    lastName: string;
  }>;
}

export const DEMO_TENANT_PASSWORD = catalog.password;
/** Alias historique — même valeur que le seed IAM. */
export const DEMO_IAM_PASSWORD = DEMO_TENANT_PASSWORD;
export const GENERIC_DEMO_SLUG = catalog.genericSlug;

export const SPECIALTY_DEMO_EMAIL_DOMAINS = catalog.emailDomains as Record<string, string>;

export const SPECIALTY_DEMO_TENANTS = catalog.tenants.map((tenant) => ({
  ...tenant,
  profileAccounts: tenant.profileAccounts ?? [],
})) as SpecialtyDemoTenant[];

/** Tous les comptes profil (droits restreints) à plat. */
export function listAllDemoProfileAccounts(): Array<
  DemoProfileAccount & { tenantSlug: string }
> {
  return SPECIALTY_DEMO_TENANTS.flatMap((tenant) =>
    tenant.profileAccounts.map((account) => ({
      ...account,
      tenantSlug: tenant.slug,
    })),
  );
}

/** Résout le tenant IAM depuis un email profil (@rh.demo, @edu.demo, …). */
export function resolveSpecialtyDemoTenantFromEmail(email: string): string | null {
  const normalized = email.trim().toLowerCase();
  const explicit = buildSpecialtyDemoEmailTenantMap()[normalized];
  if (explicit) return explicit;

  const at = normalized.lastIndexOf("@");
  if (at <= 0) return null;
  const domain = normalized.slice(at + 1);

  for (const [slug, demoDomain] of Object.entries(SPECIALTY_DEMO_EMAIL_DOMAINS)) {
    if (domain === demoDomain) return slug;
  }
  return null;
}

/** Mapping email → slug IAM pour les comptes démo métier. */
export function buildSpecialtyDemoEmailTenantMap(): Record<string, string> {
  const map: Record<string, string> = {
    "admin@itaas.demo": GENERIC_DEMO_SLUG,
    "hr@itaas.demo": GENERIC_DEMO_SLUG,
    "expired@itaas.demo": "demo-expired",
  };
  for (const tenant of SPECIALTY_DEMO_TENANTS) {
    map[tenant.adminEmail.toLowerCase()] = tenant.slug;
    for (const extra of tenant.additionalTenantAdmins ?? []) {
      map[extra.email.toLowerCase()] = tenant.slug;
    }
    for (const profile of tenant.profileAccounts) {
      map[profile.email.toLowerCase()] = tenant.slug;
    }
  }
  return map;
}

/** @deprecated Utiliser resolveSpecialtyDemoTenantFromEmail — conservé pour compat. */
export const EDU_DEMO_PORTAL_EMAILS: Record<string, string> = Object.fromEntries(
  (SPECIALTY_DEMO_TENANTS.find((t) => t.slug === "demo-edu")?.profileAccounts ?? []).map(
    (p) => [p.email.toLowerCase(), "demo-edu"],
  ),
);

export function findSpecialtyDemoBySlug(slug: string): SpecialtyDemoTenant | undefined {
  return SPECIALTY_DEMO_TENANTS.find((t) => t.slug === slug);
}

export function findSpecialtyDemoByEmail(email: string): SpecialtyDemoTenant | undefined {
  const slug = resolveSpecialtyDemoTenantFromEmail(email);
  return slug ? findSpecialtyDemoBySlug(slug) : undefined;
}

export function findDemoProfileByEmail(
  email: string,
): (DemoProfileAccount & { tenantSlug: string }) | undefined {
  const normalized = email.trim().toLowerCase();
  return listAllDemoProfileAccounts().find((p) => p.email.toLowerCase() === normalized);
}
